import { storage } from '../../ui/utils/local-storage';

export const ATTRIBUTION_STORAGE_KEY = 'k3x.attribution.first_touch';

/**
 * First-touch marketing attribution, captured once per visitor and sent on the requests that
 * produce a conversion. It is never stored in our own database.
 *
 * Two destinations, two jobs:
 *
 * - **Stripe is the campaign source of truth.** Checkout writes these fields to the Checkout
 *   Session *and* the Subscription metadata, so "trials by `utm_source` / `utm_campaign` / click
 *   id" is queryable today through the Stripe API, dashboard and Sigma.
 * - **Crazy Egg is the behavioral source.** `CE2.identify(email)` plus the API goals connect a
 *   conversion to the session recording, heatmap and A/B variant that produced it.
 *
 * The attribution fields are also sent to Crazy Egg, but that is future-proofing only: CE accepts
 * `utmParams` / `landingPage` / `referrer` / `customData` and documents them as landing in future
 * reports, so a Crazy Egg conversion that matched no recorded session cannot show its campaign
 * inside Crazy Egg today. Ask for campaign numbers, Crazy Egg for behavior.
 */
export type Attribution = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  landingPage?: string;
  referrer?: string;
  capturedAt?: string;
};

const PARAM_MAP: Record<string, keyof Attribution> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
  gclid: 'gclid',
  fbclid: 'fbclid',
  msclkid: 'msclkid',
};

/** The campaign query parameters first-touch attribution reads off a landing URL. */
export const ATTRIBUTION_URL_PARAMS: readonly string[] = Object.keys(PARAM_MAP);

// Matches the max lengths the backend schema accepts, so an oversized param can't fail the
// organization-create request.
const MAX_PARAM_LENGTH = 255;
const MAX_URL_LENGTH = 2048;

/**
 * How long a first touch stays eligible to be credited, matching the 30-day click window the ad
 * platforms report on. Past it the record is dropped and the next visit becomes the new first
 * touch — otherwise a signup a year later would still be credited to a long-dead campaign.
 */
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const isExpired = (attribution: Attribution): boolean => {
  if (!attribution.capturedAt) {
    return true;
  }

  const capturedAt = Date.parse(attribution.capturedAt);
  return Number.isNaN(capturedAt) || Date.now() - capturedAt > ATTRIBUTION_TTL_MS;
};

const read = (): Attribution | null => {
  try {
    const raw = storage.get(ATTRIBUTION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const attribution = JSON.parse(raw) as Attribution;
    if (isExpired(attribution)) {
      storage.remove(ATTRIBUTION_STORAGE_KEY);
      return null;
    }

    return attribution;
  } catch {
    return null;
  }
};

/**
 * Rebuilds the landing URL from its origin, path and the ad parameters only.
 *
 * The stored value is forwarded to Stripe and Crazy Egg, so it must never carry credentials. A
 * Firebase email-link landing (`/auth/signin?...&mode=signIn&oobCode=...`) is reached with a live
 * one-time sign-in code — completion needs a manual click, so the code can still be valid when the
 * conversion is reported. An allowlist also covers `redirectTo`, invitation tokens, and anything
 * added to a landing URL later.
 */
const sanitizeLandingPage = (href: string): string => {
  try {
    const landing = new URL(href);
    const sanitized = new URL(`${landing.origin}${landing.pathname}`);

    for (const param of Object.keys(PARAM_MAP)) {
      const value = landing.searchParams.get(param)?.trim();
      if (value) {
        sanitized.searchParams.set(param, value.slice(0, MAX_PARAM_LENGTH));
      }
    }

    return sanitized.toString().slice(0, MAX_URL_LENGTH);
  } catch {
    return '';
  }
};

/**
 * Records the ad parameters of the landing URL. First touch wins: once a record exists it is never
 * overwritten, so a later internal navigation cannot claim credit for the visit.
 */
export const captureAttribution = () => {
  try {
    if (read()) {
      return;
    }

    const params = new URLSearchParams(window.location.search);

    const captured: Attribution = {};
    for (const [param, field] of Object.entries(PARAM_MAP)) {
      const value = params.get(param)?.trim();
      if (value) {
        captured[field] = value.slice(0, MAX_PARAM_LENGTH);
      }
    }

    // Landing on an auth route with no ad parameters means the visitor followed a sign-in link from
    // their mailbox — possibly on a second device. The referrer there is the webmail host, which
    // must never be recorded as the acquisition source of a paid click.
    if (Object.keys(captured).length === 0 && window.location.pathname.startsWith('/auth/')) {
      return;
    }

    // A visit with no ad parameters and no external referrer carries nothing worth storing.
    const referrer =
      document.referrer && !document.referrer.startsWith(window.location.origin)
        ? document.referrer.slice(0, MAX_URL_LENGTH)
        : undefined;

    if (Object.keys(captured).length === 0 && !referrer) {
      return;
    }

    const landingPage = sanitizeLandingPage(window.location.href);

    storage.setJSON(ATTRIBUTION_STORAGE_KEY, {
      ...captured,
      ...(referrer && { referrer }),
      ...(landingPage && { landingPage }),
      capturedAt: new Date().toISOString(),
    } satisfies Attribution);
  } catch {
    // Storage can be unavailable (private mode, blocked site data). Attribution is best-effort.
  }
};

/** Returns the stored first touch, or undefined when there is none or it has expired. */
export const getAttribution = (): Attribution | undefined => read() ?? undefined;

/**
 * Appends the stored ad parameters to an email sign-in link. Firebase preserves the query string
 * through its `continueUrl`, so a visitor who opens the link on a second device — where
 * `localStorage` is empty — still lands with the campaign that brought them in.
 */
export const withAttributionParams = (url: string): string => {
  const attribution = read();
  if (!attribution) {
    return url;
  }

  try {
    const target = new URL(url, window.location.origin);
    for (const [param, field] of Object.entries(PARAM_MAP)) {
      const value = attribution[field];
      if (value) {
        target.searchParams.set(param, value);
      }
    }
    return target.toString();
  } catch {
    return url;
  }
};
